import React, { useState } from 'react';
import { useEventBooking } from '../../hooks/useEventBooking';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { ImageEventType, EventType, CityType, ShowroomType } from '../../types';
import { MultiStepForm, Step } from '../MultiStepForm/MultiStepForm';
import { UserInfoStep } from '../MultiStepForm/steps/UserInfoStep';
import { DateTimeStep } from '../MultiStepForm/steps/DateTimeStep';
import { ConfirmationStep } from '../MultiStepForm/steps/ConfirmationStep';
import { CompletedStep } from '../MultiStepForm/steps/CompletedStep';
import { AdditionalInfoStep } from '../MultiStepForm/steps/AdditionalInfoStep';
import { BookingConfirmation } from '../BookingConfirmation/BookingConfirmation';
import { Toast } from '../Toast/Toast';
import formIconCeu from '../../assets/icons/form-icon-ceu.svg';
import formIconInfo from '../../assets/icons/form-icon-info.svg';
import formIconConfirmNotReached from '../../assets/icons/form-icon-confirm-not-reached.svg';
import formIconConfirmReached from '../../assets/icons/form-icon-confirm-reached.svg';
import dropdownCarret from '../../assets/icons/dropdown-carret.svg';
import './EventCard.scss';

export type CardEvent = {
  id: string;
  remoteObjectId: string;
  workTypeGroupId?: string; // Add workTypeGroupId field
  title: string;
  titleFr?: string;
  type: EventType;
  duration: string;
  description: string;
  descriptionFr?: string;
  date: string;
  time: string;
  location: string;
  city: CityType;
  formType?: string;
  eventSubtype?: string;
  showroom: ShowroomType;
  imageUrl: string;
  imageEventType: ImageEventType;
};

type FormVariant = 'event' | 'appointment';

interface EventCardProps {
  event: CardEvent;
  formVariant?: FormVariant;
  onClick?: () => void;
  // isBooked property removed
  isAttended?: boolean;
}

export const EventCard: React.FC<EventCardProps> = ({ event, formVariant = 'event', onClick, isAttended = false }) => {

  const { t: eventT } = useTranslation('events');
  const { t: appointmentT } = useTranslation('appointments');
  const { bookEventAsync } = useEventBooking();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const descriptionRef = React.useRef<HTMLParagraphElement>(null);

  // Event booking tracking removed

  const handleStepComplete = (stepData: Record<string, any>) => {
    setFormData(prev => ({ ...prev, ...stepData }));
  };

  const handleBookingClick = () => {
    if (onClick) {
      onClick();
    } else {
      setShowBookingForm(true);
    }
  };


  const handleCloseForm = () => {
    setShowBookingForm(false);
  };

  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' | 'info' } | null>(null);

  const normalizeProvince = (province: string): string => {
    // Map of provinces with accents to their normalized versions
    const provinceMap: Record<string, string> = {
      'Québec': 'Quebec',
      'québec': 'Quebec',
      'QUÉBEC': 'Quebec'
    };
    return provinceMap[province] || province;
  };

  const handleFormComplete = async (data: Record<string, any>) => {
    if (formVariant === 'event') {
      setToast(null); // Clear any previous toast
      setIsSubmitting(true);
      setShowConfirmation(false); // Reset confirmation state
      
      try {
        // Determine if this is a dealer training event
        const isDealerTraining = event.formType === 'event-dealer-training';
        
        // Build base payload
        const payload: any = {
          eventId: event.remoteObjectId,
          // Required fields from EventBookingRequest
          firstname: data.firstName || '',
          lastname: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          company: data.company || '',  // For CEU events
          // Optional fields
          foodDietaryRestriction: data.foodAllergies || '',
          guestName: data.guestName || '',
          productsInterest: data.productsInterested || '',
          applianceRetailer: data.retailer || '',
          additionalComments: data.additionalComments || '',  // For public events (not CEU)
          productsRelevant: data.productsOwned || null,
          expectation: data.trainingGoals || null,  // Use trainingGoals for dealer training
          alreadyAttented: data.previousTraining || null,  // Use previousTraining for dealer training
          interestForScheduling: data.privateSession || null,  // Use privateSession for dealer training
          requireCreditReport: data.creditReporting || null,  // Map creditReporting to requireCreditReport
          brandSelled: data.currentBrands || null,  // Use currentBrands for dealer training
          tradeProfessionals: data.tradeProfessionals || '',  // For private intro class
          
          // CEU specific fields
          jobTitle: data.jobTitle || '',
          
          // Dealer Training specific fields
          companyName: data.companyName || '',
          location: data.location || '',
          
          // Add language from i18n
          language: i18n.language === 'fr' ? 'French' : 'English',
          
          // Add experienceCenter from event data - must be one of: Montreal, Toronto, Montreal - RoyalMount
          experienceCenter: event.showroom?.includes('ROYALMOUNT') ? 'Montreal - RoyalMount' : 
            (event.city === 'Montreal' ? 'Montreal' : 
              (event.city === 'Toronto' ? 'Toronto' : 'Montreal')),
          
          // Add address fields (for Lead creation - with defaults if not provided)
          addressStreet: data.streetAddress || '',
          addressPostalCode: data.postalCode || '',
          addressCity: data.city || event.city || '',
          addressState: normalizeProvince(data.province || (event.city === 'Montreal' ? 'Quebec' : 'Ontario')),
          addressCountry: 'Canada'
        };
        
        // Only add project address fields if NOT dealer training
        if (!isDealerTraining) {
          payload.projectAddressStreet = data.streetAddress || '';
          payload.projectAddressCity = data.city || '';
          payload.projectAddressPostalCode = data.postalCode || '';
          payload.projectAddressProvince = normalizeProvince(data.province || '');
        }
        
        // Use bookEvent from useEventBooking hook
        await bookEventAsync(payload);
        
        setShowConfirmation(true);
        setShowBookingForm(false);
      } catch (error: any) {
        
        setShowBookingForm(true);
        
        
        // Handle specific error cases
        if (error.response?.status === 400) {
          const errorMessage = error.response.data?.message;
          if (errorMessage?.includes('already attending')) {
            setToast({ message: eventT('form.errors.alreadyRegistered'), type: 'error' });
            handleCloseForm(); // Close the form on error
          } else {
            setToast({ message: errorMessage || eventT('form.errors.bookingFailed'), type: 'error' });
            handleCloseForm(); // Close the form on error
          }
        } else {
          setToast({ message: eventT('form.errors.bookingFailed'), type: 'error' });
          handleCloseForm(); // Close the form on error
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  // Helper to determine if additional info step should be shown
  const shouldShowAdditionalInfo = (formType: string | undefined): boolean => {
    if (!formType) return false;
    // Exclude event types, special-event, and unknown-appointment
    return !formType.startsWith('event-') && formType !== 'special-event' && formType !== 'unknown-appointment';
  };

  // Helper to get appointment title with fallback to original name if translation doesn't exist
  const getAppointmentTitle = (title: string): string => {
    if (!title) return '';
    
    // Convert title to camelCase for translation key
    const translationKey = `appointmentTitles.${title.toLowerCase().split(' ').map((word, index) => 
      index === 0 ? word : word[0].toUpperCase() + word.slice(1)
    ).join('')}`;
    
    // Try to get translation
    const translated = appointmentT(translationKey);
    
    // If translation key is returned as-is (not found), use the original title
    if (translated === translationKey || translated.startsWith('appointmentTitles.')) {
      return title.toUpperCase();
    }
    
    return translated.toUpperCase();
  };

  const bookingSteps: Step[] = (formVariant as FormVariant) === 'event' ? [
    {
      id: 'event',
      icon: { src: formIconCeu, alt: 'Event Icon' },
      title: event.title,
      isCompleted: true,
      Component: <CompletedStep onNext={() => {}} />
    },
    {
      id: 'user',
      icon: { src: formIconInfo, alt: 'User Icon' },
      title: (formVariant === 'event' ? eventT : appointmentT)('form.steps.yourInformation'),
      Component: <UserInfoStep
        onNext={(data) => {
          handleStepComplete(data);
        }}
        initialData={formData}
        formType={event.formType}
      />,
    },
    {
      id: 'confirm',
      icon: { 
        src: (isReached: boolean) => isReached ? formIconConfirmReached : formIconConfirmNotReached,
        alt: 'Confirmation Icon'
      },
      title: eventT('form.steps.reviewAndConfirm'),
      Component: <ConfirmationStep 
        eventData={event} 
        initialData={formData} 
        onNext={(data) => {
          handleStepComplete(data);
        }} 
        variant="event" 
      />
    }
  ] : [
    {
      id: 'event',
      icon: { src: formIconCeu, alt: 'Event Icon' },
      title: getAppointmentTitle(event.title),
      Component: <CompletedStep onNext={() => {}} />
    },
    {
      id: 'dateTime',
      icon: { src: formIconInfo, alt: 'DateTime Icon' },
      title: (formVariant === 'event' ? eventT : appointmentT)('form.steps.dateTime'),
      Component: <DateTimeStep onNext={handleStepComplete} initialData={formData} />
    },
    {
      id: 'userInfo',
      icon: { src: formIconInfo, alt: 'User Icon' },
      title: (formVariant === 'event' ? eventT : appointmentT)('form.steps.yourInformation'),
      Component: <UserInfoStep
        onNext={(data) => {
          handleStepComplete(data);
        }}
        initialData={formData}
        formType={event.formType}
      />,
    },
    // Conditionally include Additional Info step only if formType requires it
    ...(shouldShowAdditionalInfo(event.formType) ? [{
      id: 'additionalInfo',
      icon: { src: formIconInfo, alt: 'Additional Info Icon' },
      title: (formVariant === 'event' ? eventT : appointmentT)('form.steps.additionalInfo'),
      Component: <AdditionalInfoStep onNext={handleStepComplete} initialData={formData} formType={event.formType || ''} />
    }] : []),
    {
      id: 'confirm',
      icon: { 
        src: (isReached: boolean) => isReached ? formIconConfirmReached : formIconConfirmNotReached,
        alt: 'Confirmation Icon'
      },
      title: appointmentT('form.steps.reviewAndConfirm'),
      Component: <ConfirmationStep eventData={event} initialData={formData} onNext={handleStepComplete} variant="appointment" />
    }
  ];

  React.useEffect(() => {
    if (descriptionRef.current) {
      const element = descriptionRef.current;
      const hasOverflow = element.scrollHeight > element.clientHeight;
      setHasOverflow(hasOverflow);
    }
  }, [event.description]);

  return (
    <div className="event-card-page">
      <img src={event.imageUrl} alt={event.title} className="event-card-page__image" />
      <div className="event-card-page__content">
        <div className="event-card-page__header">
          <div className="event-card-page__date-time">
          <span>{event.date === 'Available' ? appointmentT('available') : event.date}</span>
            <span className="event-card-page__showroom">
              <span>{event.showroom}</span>
            </span>
            <span className="event-card-page__duration">
              <span>{eventT('eventCard.duration', { duration: event.duration })}</span>
            </span>
          </div>
          <span className="event-card-page__type">{eventT(`eventType.${event.type === 'In-Person' ? 'inPerson' : 'virtual'}`)}</span>
        </div>
        <div className="event-card-page__title-row">
          <h3 className="event-card-page__title">{i18n.language === 'fr' && event.titleFr ? event.titleFr : event.title}</h3>
          <button 
            className="event-card-page__info"
            onClick={() => setShowTooltip(!showTooltip)}
            aria-label="Show more information"
          >
            {showTooltip && (
              <div className="event-card-page__tooltip">
                <p>{i18n.language === 'fr' && event.descriptionFr ? event.descriptionFr : event.description}</p>
                <div className="event-card-page__location">
                  <span>{event.location}</span>
                  <span>{event.city}</span>
                  <span>{event.showroom}</span>
                </div>
              </div>
            )}
          </button>
        </div>
        <div className="event-card-page__description-container">
          <div className="event-card-page__description-row">
            <p ref={descriptionRef} className={`event-card-page__description ${isExpanded ? 'expanded' : ''}`}>
              {i18n.language === 'fr' && event.descriptionFr ? event.descriptionFr : event.description}
            </p>
            {hasOverflow && (
              <button
                className="event-card-page__expand"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-label={isExpanded ? eventT('eventCard.showLess') : eventT('eventCard.showMore')}
              >
                <img 
                  src={dropdownCarret} 
                  alt=""
                  className={'event-card-page__expand-icon ' + (isExpanded ? 'rotated' : '')}
                />
              </button>
            )}
          </div>
        </div>
        <button 
          className={`event-card-page__book ${isAttended ? 'event-card-page__book--attended' : ''}`}
          onClick={handleBookingClick}
        >
          <span>
            {isAttended ? eventT('eventCard.attended') : eventT('eventCard.bookButton')}
          </span>
          {!isAttended && <span className="event-card-page__book-arrow">→</span>}
        </button>

        {showBookingForm && (
          <div className="event-card-page__form-container">
            <MultiStepForm
              steps={bookingSteps}
              initialStep={1}
              onClose={handleCloseForm}
              onComplete={handleFormComplete}
              variant="event"
              isSubmitting={isSubmitting}
            />
          </div>
        )}

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {showConfirmation && (
          <BookingConfirmation onClose={handleCloseConfirmation} />
        )}
      </div>
    </div>
  );
};
